/*******************************************************************************************
   _____            __              ____   ____                         .__               
  /  _  \   _______/  |________  ___\   \ /   /____   ____  __ __  _____|__|____    ____  
 /  /_\  \ /  ___/\   __\_  __ \/  _ \   Y   // __ \ /    \|  |  \/  ___/  \__  \  /    \ 
/    |    \\___ \  |  |  |  | \(  <_> )     /\  ___/|   |  \  |  /\___ \|  |/ __ \|   |  \
\____|__  /____  > |__|  |__|   \____/ \___/  \___  >___|  /____//____  >__(____  /___|  /
        \/     \/                                 \/     \/           \/        \/     \/ 
********************************************************************************************
Exit Intent
********************************************************************************************

Author:     Nicholas Hamilton, PhD
Email:      nicholasehamilton@gmail.com
Date:       10th February 2024

*******************************************************************************************/
import React                            from 'react';
import moment                           from 'moment';
import exitIntent                       from 'exit-intent';
import { useLocation }                  from 'react-router-dom';
import { useCookies }                   from 'react-cookie';
import { 
    Dialog,  
    DialogContent,
}                                       from '@mui/material';
import { useDevice, useUser }           from 'contexts';
import { ExitIntentPromotion }          from 'components'
import { PreviewLocation }              from 'router/locations';
import config                           from '../config';

const OFFSET                = 10;
const STORAGE_LIFE          = 7;
const COOKIE_PATH           = config.cookies.paths.exitIntent;
const obj                   = {}

const enforceArray = value => (Array.isArray(value) ? value : [value]).filter(Boolean);

/**
 * ExitIntent component triggers a dialog when the user shows exit intent on the page.
 * It listens to mouse movements to detect when the user is about to leave the page,
 * and displays a dialog promoting content or a custom component.
 * The state of the dialog (open/closed) is controlled based on the exit intent.
 *
 * @param {Object} props - The component props.
 * @param {React.ReactNode} [props.children] - The content to render inside the dialog. If not provided, defaults to `ExitIntentPromotion`.
 * @param {number} [props.offset=OFFSET] - The offset from the edges of the window where the exit intent is triggered. This defines the area within which mouse movement is tracked.
 * @param {string} [props.storageName=COOKIE_PATH] - The name of the cookie to track if exit intent was triggered.
 * @param {number} [props.storageLife=STORAGE_LIFE] - The lifespan of the cookie (in days) that tracks the exit intent trigger.
 * @param {React.Component} [props.component=ExitIntentPromotion] - The component to display inside the dialog if `children` is not provided.
 * @param {Object} [props.componentProps=obj] - The props passed to the `component` (if provided).
 * @param {Object} [props.dialogProps=obj] - The props passed to the `Dialog` component.
 * @param {string|string[]} [props.abortPaths=PreviewLocation.path] - A single path or an array of paths that will trigger abort behavior if the current pathname includes any of them.
 * @param {string|string[]} [props.applyPaths] - A single path or an array of paths where the exit intent should **apply**. If empty or undefined, this is ignored and the exit intent applies to all paths.
 * 
 * @returns {React.Element|null} - Returns the dialog component when exit intent is triggered, otherwise `null`.
 */
export const ExitIntent = ({
    children,
    offset                  = OFFSET, 
    storageName             = COOKIE_PATH, 
    storageLife             = STORAGE_LIFE,
    component : Component   = ExitIntentPromotion,
    componentProps          = obj,
    dialogProps             = obj,
    abortPaths              = PreviewLocation.path,     // Accepts a single value or an array of values
    applyPaths              = undefined                 // Default to undefined (empty/undefined behavior)
}) => {

    const { isNative }          = useDevice();
    const [cookies, setCookie]  = useCookies([storageName]); 
    const {isAuthenticated}     = useUser();
    const {pathname}            = useLocation();

    const [open,    setOpen]    = React.useState(!false);
    const [entered, setEntered] = React.useState(false);
    
    const [
        exitIntentTriggered, 
        setExitIntentTriggered
    ]                           = React.useState(() => cookies[storageName] === 'true' );

    // Abort or Not
    const shouldAbort = React.useMemo(() => {
        const arr = enforceArray(abortPaths);
        return arr.some(pattern => (pathname || '').includes(pattern));
    }, [abortPaths, pathname]);

    // Apply or Not
    const shouldApply = React.useMemo(() => {
        const arr = enforceArray(applyPaths);
        return !arr.length || arr.some(pattern => (pathname || '').includes(pattern));
    }, [applyPaths, pathname]);

     // Open Dialog
    const handleOpen            = React.useCallback( () => setOpen(true), []);

    // Close Dialog
    const handleClose           = React.useCallback( () => setOpen(false), []);

    // Effect to close on page change
    React.useEffect(() => setOpen(false), [pathname])

    // Keep in sync with cookie 
    // (in case it expires or is removed externally)
    React.useEffect(() => {
        const cookieValue = cookies[storageName] === 'true';
        setExitIntentTriggered(cookieValue);
    }, [cookies, storageName]);

    // Effect to track mouse entry
    React.useEffect(() => {
        
        // Once Entered, No need to track
        if(shouldAbort || !shouldApply || entered || exitIntentTriggered) 
            return;

        // Handle Mouse Move, Set Enter if Offset is Breached.
        const handleMouseMove = (e) => {
            const { clientX, clientY }          = e;
            const { innerWidth, innerHeight }   = window;
            const withinX = clientX > offset && clientX < innerWidth  - offset;
            const withinY = clientY > offset && clientY < innerHeight - offset;
            if (withinX && withinY) {
                setEntered(true);
            }
        };

        document.addEventListener('mousemove', handleMouseMove);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
        };

    }, [shouldAbort, exitIntentTriggered, entered, offset, shouldApply]);

    React.useEffect(() => {

        // If not entered, no exit intent
        if(shouldAbort || !shouldApply || !entered)
            return;

        const handleExitIntent = () => {
            
            // Mark Open
            handleOpen();
            
            // Set a cookie to track that the exit intent has been triggered, Expires in 1 day
            // Ensure Cookie Consent is Set
            if(storageLife > 0){
                setCookie(
                    storageName, 
                    'true', 
                    { 
                        path    : '/', 
                        expires : moment().add(storageLife, 'day').toDate()
                    }
                );
                setExitIntentTriggered(true);
            }
        }

        // Initialise
        const removeExitIntent = exitIntent({
            threshold       : Math.max(offset - 1,0),
            maxDisplays     : 1,
            eventThrottle   : 100,
            onExitIntent    : handleExitIntent  
        });

        // Cleanup
        return () => {
            removeExitIntent()
        }

    },[shouldAbort, entered, handleOpen, offset, pathname, setCookie, storageLife, storageName, shouldApply])

    // Not for Mobile
    if(isNative)
        return null;

    // Not if Authentiated
    if(isAuthenticated)
        return null;

    // Aborted
    if(shouldAbort)
        return null;

    // Aborted
    if(!shouldApply)
        return null;

    // Not Open
    if(!open)
        return null;

    // No children or component
    if(!children || !Component)
        return null;
  
    return (
        <Dialog 
            maxWidth    = "xs" 
            fullWidth   = {true} 
            {...dialogProps} 
            open        = {open} 
            onClose     = {handleClose} 
        >
            <DialogContent>
                {
                    children || 
                    <Component {...componentProps}/> 
                }
            </DialogContent>
        </Dialog>
    )
}

export default ExitIntent;